import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, RefreshCw, Flame, ChevronDown, ClipboardList, Utensils, Layout, Image, MessageSquare, ShoppingBag } from "lucide-react";
import { adminLogin, adminListOrders, adminUpdateStatus } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AdminMenuManager } from "@/components/AdminMenuManager";
import { AdminSiteContent } from "@/components/AdminSiteContent";
import { AdminGallery } from "@/components/AdminGallery";
import { AdminReviews } from "@/components/AdminReviews";
import { AdminMerch } from "@/components/AdminMerch";

const STORAGE_KEY = "hb_admin_token";
const STATUSES = ["received", "preparing", "ready", "completed", "cancelled"];

const statusColor = (s) => {
  switch (s) {
    case "pending_payment": return "bg-yellow-900/40 text-yellow-300 border-yellow-700";
    case "received": return "bg-blue-900/40 text-blue-300 border-blue-700";
    case "preparing": return "bg-orange-900/40 text-orange-300 border-orange-700";
    case "ready": return "bg-green-900/40 text-green-300 border-green-700";
    case "completed": return "bg-[#E60000]/20 text-[#FF6B6B] border-[#E60000]";
    case "cancelled": return "bg-gray-900/60 text-gray-400 border-gray-700";
    default: return "bg-gray-900/40 text-gray-300 border-gray-700";
  }
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [tab, setTab] = useState("orders");

  const load = async (t) => {
    setLoading(true);
    try {
      const data = await adminListOrders(t || token);
      setOrders(data.orders || []);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) {
        toast.error("Session expired. Log in again.");
        setToken("");
        localStorage.removeItem(STORAGE_KEY);
      } else {
        toast.error("Couldn't load orders");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const data = await adminLogin(password, email);
      setToken(data.token);
      localStorage.setItem(STORAGE_KEY, data.token);
      setPassword("");
    } catch {
      toast.error("Invalid credentials");
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await adminUpdateStatus(orderId, status, token);
      toast.success(`Marked as ${status}`);
      load(token);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const logout = () => {
    setToken("");
    localStorage.removeItem(STORAGE_KEY);
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4">
        <form onSubmit={handleLogin} className="max-w-md w-full bg-[#1A1A1A] border-2 border-[#333] p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#E60000] flex items-center justify-center"><Lock className="w-5 h-5" strokeWidth={2.5} /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold">Restricted</div>
              <h1 className="font-heading text-3xl">Admin Login</h1>
            </div>
          </div>
          <label className="text-xs uppercase tracking-widest text-white/70 mb-2 block">Email</label>
          <Input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@hillboyzmemphis.com"
            className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-12 text-white mb-4"
          />
          <label className="text-xs uppercase tracking-widest text-white/70 mb-2 block">Password</label>
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-12 text-white mb-5"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            className="w-full h-12 bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-widest text-sm border-2 border-transparent hover:border-white"
          >
            Enter
          </button>
          <Link to="/" className="block mt-4 text-center text-xs uppercase tracking-widest text-white/40 hover:text-[#E60000]">
            ← Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b-2 border-[#E60000] bg-black sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#E60000] flex items-center justify-center"><Flame className="w-5 h-5" strokeWidth={2.5} /></div>
            <div className="hb-logo-text text-3xl">HILL<span className="accent">BOYZ</span></div>
            <div className="font-heading text-xl text-white/60 mt-1">· Admin</div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="refresh-orders-btn"
              onClick={() => load()}
              className="inline-flex items-center gap-2 bg-transparent border-2 border-[#333] hover:border-[#E60000] text-white px-4 h-10 uppercase tracking-widest text-xs font-bold"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="bg-[#E60000] hover:bg-[#FF3333] text-white px-4 h-10 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white"
            >
              Logout
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-[#222] overflow-x-auto">
          <TabBtn active={tab === "orders"}  onClick={() => setTab("orders")}  testId="tab-orders"  icon={ClipboardList}>Orders</TabBtn>
          <TabBtn active={tab === "menu"}    onClick={() => setTab("menu")}    testId="tab-menu"    icon={Utensils}>Menu</TabBtn>
          <TabBtn active={tab === "content"} onClick={() => setTab("content")} testId="tab-content" icon={Layout}>Site Content</TabBtn>
          <TabBtn active={tab === "gallery"} onClick={() => setTab("gallery")} testId="tab-gallery" icon={Image}>Gallery</TabBtn>
          <TabBtn active={tab === "reviews"} onClick={() => setTab("reviews")} testId="tab-reviews" icon={MessageSquare}>Reviews</TabBtn>
          <TabBtn active={tab === "merch"}   onClick={() => setTab("merch")}   testId="tab-merch"   icon={ShoppingBag}>Merch</TabBtn>
        </div>

        {tab === "menu" ? (
          <AdminMenuManager token={token} />
        ) : tab === "content" ? (
          <AdminSiteContent token={token} />
        ) : tab === "gallery" ? (
          <AdminGallery token={token} />
        ) : tab === "reviews" ? (
          <AdminReviews token={token} />
        ) : tab === "merch" ? (
          <AdminMerch token={token} />
        ) : (
        <>
        <div className="mb-8">
          <h2 className="font-heading text-4xl md:text-5xl mb-2">Orders</h2>
          <p className="text-white/60 text-sm uppercase tracking-widest">{orders.length} total · live</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {["pending_payment", "received", "preparing", "ready", "completed", "cancelled"].map((s) => {
            const count = orders.filter((o) => o.status === s).length;
            return (
              <div key={s} data-testid={`stat-${s}`} className="border border-[#333] bg-[#1A1A1A] p-4">
                <div className="text-xs uppercase tracking-widest text-white/60 mb-1">{s.replace("_", " ")}</div>
                <div className="font-heading text-3xl text-white">{count}</div>
              </div>
            );
          })}
        </div>
        {loading && orders.length === 0 ? (
          <div className="text-center py-20 text-white/50 uppercase tracking-widest">Loading orders…</div>
        ) : orders.length === 0 ? (
          <div data-testid="admin-no-orders" className="text-center py-20 text-white/50 uppercase tracking-widest border border-[#333] bg-[#1A1A1A]">
            No orders yet. Time to spread the word.
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => {
              const isOpen = expanded[o.id];
              return (
                <article
                  key={o.id}
                  data-testid={`admin-order-${o.id}`}
                  className="border border-[#333] bg-[#1A1A1A] hover:border-[#E60000] transition-colors"
                >
                  <button
                    data-testid={`toggle-order-${o.id}`}
                    onClick={() => setExpanded((e) => ({ ...e, [o.id]: !e[o.id] }))}
                    className="w-full grid grid-cols-12 gap-3 items-center px-4 py-4 text-left"
                  >
                    <div className="col-span-6 sm:col-span-3">
                      <div className="text-[10px] uppercase tracking-widest text-white/50">Order</div>
                      <div className="font-mono text-sm">{o.id.slice(0, 8)}</div>
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <div className="text-[10px] uppercase tracking-widest text-white/50">Customer</div>
                      <div className="text-sm font-semibold truncate">{o.customer_name}</div>
                      <div className="text-xs text-white/60">{o.customer_phone}</div>
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <div className="text-[10px] uppercase tracking-widest text-white/50">Total</div>
                      <div className="font-heading text-xl">${o.total?.toFixed(2)}</div>
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <div className="text-[10px] uppercase tracking-widest text-white/50">Type</div>
                      <div className="text-sm uppercase">{o.fulfillment}</div>
                    </div>
                    <div className="col-span-4 sm:col-span-2 flex items-center justify-end gap-2">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${statusColor(o.status)}`}>
                        {o.status?.replace("_", " ")}
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {isOpen && (
                    <div data-testid={`order-detail-${o.id}`} className="border-t border-[#333] px-4 py-5 grid md:grid-cols-12 gap-6">
                      <div className="md:col-span-7">
                        <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-3">Items</div>
                        <ul className="space-y-2 text-sm">
                          {o.lines?.map((l, i) => (
                            <li key={i} className="flex justify-between border-b border-[#222] pb-2">
                              <span>{l.quantity}× {l.name}</span>
                              <span className="text-white/70">${l.line_total?.toFixed(2)}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                          <div><div className="text-[10px] uppercase tracking-widest text-white/50">Subtotal</div><div>${o.subtotal?.toFixed(2)}</div></div>
                          <div><div className="text-[10px] uppercase tracking-widest text-white/50">Tax</div><div>${o.tax?.toFixed(2)}</div></div>
                          <div><div className="text-[10px] uppercase tracking-widest text-white/50">Total</div><div className="font-bold">${o.total?.toFixed(2)}</div></div>
                        </div>
                      </div>
                      <div className="md:col-span-5 space-y-3 text-sm">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-white/50">Phone</div>
                          <a href={`tel:${o.customer_phone}`} className="text-[#FF6B6B] hover:text-[#E60000]">{o.customer_phone}</a>
                        </div>
                        {o.customer_email && (
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-white/50">Email</div>
                            <div>{o.customer_email}</div>
                          </div>
                        )}
                        {o.delivery_address && (
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-white/50">Address</div>
                            <div>{o.delivery_address}</div>
                          </div>
                        )}
                        {o.notes && (
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-white/50">Notes</div>
                            <div className="italic text-white/80">{o.notes}</div>
                          </div>
                        )}
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Update status</div>
                          <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                            <SelectTrigger
                              data-testid={`status-trigger-${o.id}`}
                              className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white uppercase tracking-wider text-xs"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1A1A1A] border-2 border-[#333] text-white rounded-none">
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`status-option-${o.id}-${s}`} className="uppercase text-xs tracking-wider">
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
};

export default Admin;

const TabBtn = ({ active, onClick, testId, icon: Icon, children }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 sm:px-5 h-11 uppercase tracking-widest text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${
      active ? "border-[#E60000] text-white" : "border-transparent text-white/50 hover:text-white"
    }`}
  >
    <Icon className="w-4 h-4" /> {children}
  </button>
);
